import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { getUserProfile } from "../services/authAPI";
import { getLoginData } from "../utility/AppStoreData";
import { toast } from "react-toastify";
import { registerUserDataContextClear } from "../utility/cacheManager";

interface UserData {
  AccessToken: string;
  IdToken: string;
  accessTokenExpiresIn: number;
  idTokenExpiresIn: number;
  message: string;
  username: string;
  userID?: string;
  profilePicture?: string;
  coins?: number;
  balance?: number;
  referralCode?: string;
  dailyCheckInResult?: any;
  socialLinks?: {
    instagramLink?: string;
    xLink?: string;
    facebookLink?: string;
  };
  // Blitz platform tokens (from login response)
  blitz?: {
    refreshToken: string;
    token: string;
    tokenExpires: number;
  } | null;
}

interface UserDataContextType {
  userData: UserData | null;
  userProfileData: any;
  accessToken: string;
  isLoading: boolean;
  isProfileLoading: boolean;
  error: string | null;
  fetchUserData: () => Promise<void>;
  fetchUserProfile: (token?: string) => Promise<void>;
  updateUserData: (data: Partial<UserData>) => void;
  clearUserData: () => void;
}

const UserDataContext = createContext<UserDataContextType | undefined>(
  undefined
);

export const useUserData = () => {
  const context = useContext(UserDataContext);
  if (context === undefined) {
    throw new Error("useUserData must be used within a UserDataProvider");
  }
  return context;
};

export const UserDataProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [userData, setUserData] = useState<UserData | null>(null);
  const [userProfileData, setUserProfileData] = useState<any>(null);
  const [accessToken, setAccessToken] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isProfileLoading, setIsProfileLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUserData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await getLoginData();
      if (data) {
        setUserData(data);
        setAccessToken(data.AccessToken || "");

        if (data.AccessToken) {
          await fetchUserProfile(data.AccessToken);
        }
      }
    } catch (err: any) {
      setError(err.message || "Failed to fetch user data");
      toast.error(err.message || "Failed to fetch user data");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchUserProfile = useCallback(
    async (token?: string) => {
      const tokenToUse = token || accessToken;
      if (!tokenToUse) return;

      setIsProfileLoading(true);
      setError(null);

      try {
        const data = await getUserProfile(tokenToUse);
        const responseBody =
          typeof data.body === "string" ? JSON.parse(data.body) : data.body;

        if (data.statusCode === 200) {
          setUserProfileData(responseBody);
        } else if (data.statusCode === 400) {
          throw new Error("Bad Request");
        } else if (data.statusCode > 400 && data.statusCode < 420) {
          throw new Error("Unauthorized");
        } else if (data.statusCode >= 500) {
          throw new Error("Internal Server Error");
        }
      } catch (err: any) {
        setError(err.message || "Failed to fetch user profile");
        toast.error(err.message || "Failed to fetch user profile");
      } finally {
        setIsProfileLoading(false);
      }
    },
    [accessToken]
  );

  const updateUserData = useCallback((data: Partial<UserData>) => {
    setUserData((prev) => (prev ? { ...prev, ...data } : (data as UserData)));
  }, []);

  const clearUserData = useCallback(() => {
    console.log('🧹 Clearing UserDataContext state...');
    setUserData(null);
    setUserProfileData(null);
    setAccessToken("");
    setError(null);
    setIsLoading(false);
    setIsProfileLoading(false);
    console.log('✅ UserDataContext state cleared');
  }, []);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // Register clearUserData function with cacheManager on mount
  useEffect(() => {
    registerUserDataContextClear(clearUserData);
  }, [clearUserData]);

  const contextValue: UserDataContextType = {
    userData,
    userProfileData,
    accessToken,
    isLoading,
    isProfileLoading,
    error,
    fetchUserData,
    fetchUserProfile,
    updateUserData,
    clearUserData,
  };

  return (
    <UserDataContext.Provider value={contextValue}>
      {children}
    </UserDataContext.Provider>
  );
};
